import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

export type ColorProfileId =
  | 'parchment'
  | 'chalk'
  | 'linen'
  | 'stone'
  | 'mist'
  | 'pewter'
  | 'slate'
  | 'sage'
  | 'ink'
  | 'obsidian';

export type ColorProfile = {
  id: ColorProfileId;
  label: string;
  scheme: 'light' | 'dark';
  surface: string;
  bg: string;
  inkColor: string;
};

export const COLOR_PROFILES: readonly ColorProfile[] = [
  // Light (5)
  { id: 'parchment', label: 'Parchment', scheme: 'light', surface: '#f5eed9', bg: '#f0e8d4', inkColor: '#241d10' },
  { id: 'chalk',     label: 'Chalk',     scheme: 'light', surface: '#fdfbf5', bg: '#faf7f0', inkColor: '#1f1c14' },
  { id: 'linen',     label: 'Linen',     scheme: 'light', surface: '#f1ead9', bg: '#ebe3d0', inkColor: '#211b10' },
  { id: 'stone',     label: 'Stone',     scheme: 'light', surface: '#e4ddd0', bg: '#ddd6c9', inkColor: '#1c1812' },
  { id: 'mist',      label: 'Mist',      scheme: 'light', surface: '#e5e7e3', bg: '#dee0dc', inkColor: '#171a17' },
  // Dark (5)
  { id: 'pewter',    label: 'Pewter',    scheme: 'dark',  surface: '#2c2a24', bg: '#26241f', inkColor: '#ece4d0' },
  { id: 'sage',      label: 'Sage',      scheme: 'dark',  surface: '#27302a', bg: '#222a25', inkColor: '#dde3d8' },
  { id: 'slate',     label: 'Slate',     scheme: 'dark',  surface: '#2c323a', bg: '#262b32', inkColor: '#e3e6ea' },
  { id: 'ink',       label: 'Ink',       scheme: 'dark',  surface: '#1a1c22', bg: '#15171c', inkColor: '#ddd9d0' },
  { id: 'obsidian',  label: 'Obsidian',  scheme: 'dark',  surface: '#0d0d10', bg: '#08080a', inkColor: '#d6cfbf' },
];

const STORAGE_KEY = '_cp2';
const VALID_IDS = new Set<string>(COLOR_PROFILES.map((p) => p.id));
const DEFAULT_PROFILE: ColorProfileId = 'parchment';

// Migrate old IDs to new ones
const MIGRATIONS: Record<string, ColorProfileId> = {
  cream: 'linen',
  midnight: 'pewter',
  charcoal: 'ink',
  forest: 'sage',
};

function loadProfile(): ColorProfileId {
  if (typeof localStorage === 'undefined') return DEFAULT_PROFILE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_IDS.has(raw)) return raw as ColorProfileId;
    // Try old key
    const old = localStorage.getItem('_cp1');
    if (old) {
      const migrated = MIGRATIONS[old] ?? (VALID_IDS.has(old) ? (old as ColorProfileId) : null);
      if (migrated) return migrated;
    }
    // Legacy binary theme key
    const oldTheme = localStorage.getItem('_th1');
    if (oldTheme === 'dark') return 'pewter';
  } catch {
    /* ignore */
  }
  return DEFAULT_PROFILE;
}

export function getProfile(id: ColorProfileId): ColorProfile {
  return COLOR_PROFILES.find((p) => p.id === id) ?? COLOR_PROFILES[0]!;
}

export const colorProfile = signal<ColorProfileId>(loadProfile());

export function setColorProfile(id: ColorProfileId): void {
  colorProfile.value = id;
}

if (typeof window !== 'undefined') {
  effect(() => {
    const id = colorProfile.value;
    const profile = getProfile(id);
    void secureStorage.setItem(STORAGE_KEY, id);
    document.documentElement.setAttribute('data-color-profile', id);
    // data-theme drives legacy [data-theme='dark'] rules
    document.documentElement.setAttribute('data-theme', profile.scheme === 'dark' ? 'dark' : 'light');
    // color-scheme for native browser controls
    document.documentElement.style.colorScheme = profile.scheme;
    // PWA chrome color
    const meta = document.getElementById('theme-color-meta');
    if (meta) meta.setAttribute('content', profile.surface);
  });
}
